import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { authAxios } from '../../services/crmApi';
import type { useApplicationDetail } from '../../hooks/useApplicationDetail';

interface PKDLookupResult {
  valid: boolean;
  code: string;
  status: 'current' | 'legacy' | 'unknown';
  message: string;
  suggestedCode?: string;
  suggestedName?: string;
  name?: string;
}

const PKDValidationBadge: React.FC<{ code: string; onSuggest?: (code: string) => void }> = ({ code, onSuggest }) => {
  const [result, setResult] = useState<PKDLookupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!code || code.trim().length < 4) {
      setResult(null);
      return;
    }
    setLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await authAxios.get(`/api/crm/pkd/lookup/${encodeURIComponent(code.trim())}`);
        setResult(res.data);
      } catch {
        setResult(null);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [code]);

  if (!code || code.trim().length < 4 || loading) return null;
  if (!result) return null;

  if (result.status === 'current') {
    return (
      <div className="flex items-center gap-1.5 mt-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
        <CheckCircle size={14} />
        <span>{result.message}</span>
      </div>
    );
  }
  if (result.status === 'legacy') {
    return (
      <div className="mt-1 text-xs text-amber-700 bg-amber-50 px-2 py-1.5 rounded space-y-1">
        <div className="flex items-center gap-1.5">
          <AlertTriangle size={14} />
          <span>{result.message}</span>
        </div>
        {result.suggestedCode && onSuggest && (
          <button
            onClick={() => onSuggest(result.suggestedCode!)}
            className="text-amber-800 underline hover:text-amber-900 font-medium"
          >
            Zastosuj sugerowany kod: {result.suggestedCode}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 mt-1 text-xs text-red-700 bg-red-50 px-2 py-1 rounded">
      <XCircle size={14} />
      <span>{result.message}</span>
    </div>
  );
};

interface Props { h: ReturnType<typeof useApplicationDetail>; }

export const BusinessProfileSection: React.FC<Props> = ({ h }) => {
  const { application, editMode, editedData, generatingBusinessProfile } = h;
  if (!application) return null;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center">
          Profil Biznesowy
          {editMode && <span className="ml-3 text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">Tryb edycji</span>}
        </h2>
        {!editMode && (
          <button onClick={h.handleGenerateBusinessProfileAI} disabled={generatingBusinessProfile} className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 disabled:bg-gray-400 flex items-center shadow-lg">
            <Sparkles className="mr-2" size={18} />
            {generatingBusinessProfile ? 'Generowanie AI...' : 'AI Sugestia dla Operatora'}
          </button>
        )}
      </div>
      {!editMode ? (
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">Surowy opis pomysłu</p>
            <p className="text-sm bg-gray-50 p-3 rounded">{application.business_idea_raw}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">PKD</p>
              <p className="font-medium">{application.pkd_code}</p>
              {application.pkd_code && <PKDValidationBadge code={application.pkd_code} />}
            </div>
            <div>
              <p className="text-sm text-gray-600">Branża</p>
              <p className="font-medium">{application.industry}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Tytuł do lokalu</p>
              <p className="font-medium">{application.premises_title || 'Nie podano'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Adres lokalu</p>
              <p className="font-medium">{application.premises_address || 'Nie podano'}</p>
            </div>
            {application.requested_amount && application.requested_amount !== 35000 && !['hutnik_biznes', 'marketing_beauty', 'gastro_consulting', 'wycieczki', 'turnusy', 'bhp', 'zus', 'uslugi_ksiegowe', 'krs'].includes(application.service_type || '') && (
            <div className="col-span-2">
              <p className="text-sm text-gray-600">Kwota wnioskowana</p>
              <p className="font-medium text-lg text-blue-600">{(application.requested_amount || 0).toLocaleString('pl-PL')} zł</p>
            </div>
            )}
          </div>
          {application.operator_notes && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm font-medium text-yellow-800 mb-1">Notatki operatora</p>
              <p className="text-sm text-yellow-900 whitespace-pre-wrap">{application.operator_notes}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Opis pomysłu biznesowego</label>
            <textarea value={editedData.business_idea_raw || ''} onChange={(e) => h.handleEditFieldChange('business_idea_raw', e.target.value)} className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 min-h-32" placeholder="Opisz pomysł na działalność gospodarczą..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-yellow-800 mb-1">Notatki operatora</label>
            <textarea value={editedData.operator_notes || ''} onChange={(e) => h.handleEditFieldChange('operator_notes', e.target.value)} className="w-full p-3 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 bg-yellow-50 min-h-24" placeholder="Notatki wewnętrzne dla operatora..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kod PKD</label>
              <input type="text" value={editedData.pkd_code || ''} onChange={(e) => h.handleEditFieldChange('pkd_code', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="np. 62.01.Z" />
              <PKDValidationBadge
                code={editedData.pkd_code || ''}
                onSuggest={(code) => h.handleEditFieldChange('pkd_code', code)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Branża</label>
              <select value={editedData.industry || ''} onChange={(e) => h.handleEditFieldChange('industry', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="">Wybierz...</option>
                <option value="production">Produkcja (+2 pkt)</option>
                <option value="services">Usługi (+1 pkt)</option>
                <option value="trade">Handel (+1 pkt)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tytuł do lokalu</label>
              <select value={editedData.premises_title || ''} onChange={(e) => h.handleEditFieldChange('premises_title', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="">Wybierz...</option>
                <option value="ownership">Własność</option>
                <option value="rent">Najem</option>
                <option value="lease">Dzierżawa</option>
                <option value="free_use">Użyczenie</option>
                <option value="mobile">Działalność mobilna</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kwota wnioskowana (zł)</label>
              <input type="number" value={editedData.requested_amount || ''} onChange={(e) => h.handleEditFieldChange('requested_amount', parseInt(e.target.value) || 0)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" min="0" max="35000" step="100" />
              <p className="text-xs text-gray-500 mt-1">Maksimum: 35 000 zł</p>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Adres lokalu działalności</label>
              <input type="text" value={editedData.premises_address || ''} onChange={(e) => h.handleEditFieldChange('premises_address', e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="ul. Przykładowa 123, 31-000 Kraków" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
